/**
 * UnifiedFabManager - Cross-Zone Drag-and-Drop FAB System
 * 
 * Manages all FABs across FOUR zones:
 * - Top Toolbar (horizontal)
 * - Left Column (vertical) - NEW
 * - Right Column (vertical)
 * - Bottom Footer (horizontal)
 * 
 * Features:
 * - Drag FABs between ANY zone
 * - Reorder within zones
 * - UNLIMITED FABs per zone (scrolls when needed)
 * - Zone-aware mini-FAB popout direction
 * - 60px uniform size with 5px border for glove-friendly tapping
 * - localStorage persistence
 */

import React, { useState, useEffect, useCallback, useRef, useMemo, ReactNode } from 'react';
import { Box, IconButton, Tooltip, Badge, Typography, Snackbar, Alert } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import SettingsIcon from '@mui/icons-material/Settings';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import ContrastIcon from '@mui/icons-material/Contrast';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import SearchIcon from '@mui/icons-material/Search';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import MapIcon from '@mui/icons-material/Map';
import GroupIcon from '@mui/icons-material/Group';
import RouteIcon from '@mui/icons-material/Route';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BuildIcon from '@mui/icons-material/Build';
import ShareIcon from '@mui/icons-material/Share';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import WarningIcon from '@mui/icons-material/Warning';
import LocalPoliceIcon from '@mui/icons-material/LocalPolice';
import CarCrashIcon from '@mui/icons-material/CarCrash';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import BlockIcon from '@mui/icons-material/Block';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import FeedbackIcon from '@mui/icons-material/Feedback';
import WifiIcon from '@mui/icons-material/Wifi';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import SignalWifi4BarIcon from '@mui/icons-material/SignalWifi4Bar';
import SyncIcon from '@mui/icons-material/Sync';
import TimerIcon from '@mui/icons-material/Timer';
import GroupsIcon from '@mui/icons-material/Groups';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import WorkIcon from '@mui/icons-material/Work';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { feedbackService } from '../services/feedback';
import { requestPreciseLocation } from '../services/highPrecisionLocation';

import { telegram } from '../services/telegram';
import { roadHazardsService, RoadHazard } from '../services/roadHazards';
import { getCyberColors } from '../theme/cyberColors';
import { useM3Motion } from '../hooks/useM3Motion';
import { useTranslation } from '../i18n/translations';

const withAlpha = (hexColor: string, alpha: number): string => {
  const clean = hexColor.replace('#', '');
  const normalized = clean.length === 3
    ? clean.split('').map(char => char + char).join('')
    : clean;
  const base = normalized.slice(0, 6);
  const intval = parseInt(base, 16);
  if (Number.isNaN(intval)) return hexColor;
  const r = (intval >> 16) & 255;
  const g = (intval >> 8) & 255;
  const b = intval & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// ============================================================================
// TYPES
// ============================================================================

export type FabZone = 'top' | 'left' | 'right' | 'bottom';
export type LockedPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | null;

interface ZoneLayout {
  top: string[];
  left: string[];
  right: string[];
  bottom: string[];
  archived: string[];
}

// Mini-FAB popout direction based on zone
export const getPopoutDirection = (zone: FabZone): 'up' | 'down' | 'left' | 'right' => {
  switch (zone) {
    case 'top': return 'down';
    case 'bottom': return 'up';
    case 'left': return 'right';
    case 'right': return 'left';
  }
};

interface UnifiedFabManagerProps {
  visible: boolean;
  onOpenDrawer: () => void;
  onShowSettings: () => void;
  showSettingsPopout: boolean;
  onFavoritesClick: () => void;
  favoritesCount: number;
  // Voice
  voiceStatus: 'idle' | 'listening' | 'processing' | 'success' | 'error';
  onVoiceClick: () => void;
  // Search
  onSearchClick: () => void;
  // Manual Search
  onManualSearchClick: () => void;
  // Map
  onMapClick: () => void;
  // Crews
  onCrewsClick: () => void;
  // Routes
  onRoutesClick: () => void;
  // Caliche Routes (landmark-based routes)
  onCalicheRoutesClick?: () => void;
  // Activity
  onActivityClick: () => void;
  // Services
  onServicesClick: () => void;
  // Facilities View
  onFacilitiesViewClick?: () => void;
  // Hazards View
  onHazardsViewClick?: () => void;
  // Community Facilities
  onCommunityFacilitiesClick?: () => void;
  communityFacilitiesCount?: number;
  // Jobs Board
  onJobsClick: () => void;
  // Friends
  onFriendsClick: () => void;
  // Basin Community
  onBasinCommunityClick?: () => void;
  // Cop Alert (report police location to all users)
  onCopAlert?: () => void;
  // Safety Inspector Alert (report safety inspector location to all users)
  onReportSafetyGuy?: () => void;
  // Road Hazard Reporting - directly reports with location
  onHazardReported?: (hazard: RoadHazard) => void;
  // QR Code sharing
  onShowQrCode?: () => void;
  // Suggestions
  onShowSuggestions?: () => void;
  // Premium/Subscription
  onPremiumClick: () => void;
  isSubscribed?: boolean;
  // Network status
  networkStatus?: 'online' | 'offline' | 'poor';
  // Home reset
  onHomeReset: () => void;
  // Settings popout content
  darkMode: boolean;
  onToggleDarkMode: () => void;
  highContrast: boolean;
  onToggleHighContrast: () => void;
  language: string;
  onToggleLanguage: () => void;
  // Zia Gives Back - charity/giving feature
  onOpenZiaGivesBack?: () => void;
  // SpeedDial components (rendered as children)
  aiSpeedDial: ReactNode;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const STORAGE_KEY = 'fab_zone_layout';
const TOOLBAR_VISIBILITY_KEY = 'unified_fab_toolbar_visibility';
const LAYOUT_VERSION = 33; // v33: Archive crews, amigos, services, basin community, caliche routes FABs
const FAB_SIZE = 64;
const ICON_SIZE = 32;
const FAB_BORDER_WIDTH = 5; // Increased from 3px for better visibility
const LONG_PRESS_DURATION = 500;
const EMERGENCY_COUNTDOWN_SECONDS = 5;
const COP_COUNTDOWN_SECONDS = 3;
const TOOLBAR_HEIGHT = FAB_SIZE + 32;
const FOOTER_HEIGHT = FAB_SIZE + 38;
const VERTICAL_COLUMN_WIDTH = FAB_SIZE + 24;
const VERTICAL_TOP_OFFSET = FAB_SIZE + 44;
const VERTICAL_BOTTOM_OFFSET = FAB_SIZE + 52;
const ARROW_INDICATOR_SIZE = 56;
const ARROW_ICON_SIZE = 32;

// Toolbar visibility interface
interface ToolbarVisibility {
  top: boolean;
  left: boolean;
  right: boolean;
  bottom: boolean;
}

const DEFAULT_VISIBILITY: ToolbarVisibility = {
  top: true,
  left: true,
  right: true,
  bottom: true,
};

const DEFAULT_LAYOUT: ZoneLayout = {
  top: ['hamburger', 'settings', 'trash'],
  left: ['support', 'manualSearch'],
  right: ['search', 'ai', 'favorites', 'map', 'calicheRoutes', 'facilitiesView', 'hazardsView'],
  bottom: ['premium', 'ziaGivesBack', 'emergency911'],
  archived: ['activity', 'jobs', 'suggestions', 'timer', 'networkStatus', 'qrShare', 'voice', 'services', 'basin-community', 'crews', 'friends', 'safetyGuy'],
};

// ============================================================================
// COMPONENT
// ============================================================================

export function UnifiedFabManager(props: UnifiedFabManagerProps) {
  const { t } = useTranslation();
  
  const {
    visible,
    onOpenDrawer,
    onShowSettings,
    showSettingsPopout,
    onFavoritesClick,
    favoritesCount,
    voiceStatus,
    onVoiceClick,
    onSearchClick,
    onManualSearchClick,
    onMapClick,
    onCrewsClick,
    // onRoutesClick,
    onCalicheRoutesClick,
    onFacilitiesViewClick,
    onHazardsViewClick,
    onActivityClick,
    onServicesClick,
    onJobsClick,
    onFriendsClick,
    onBasinCommunityClick,
    onCopAlert,
    onReportSafetyGuy,
    onCommunityFacilitiesClick,
    communityFacilitiesCount = 0,
    onHazardReported,
    onShowQrCode,
    onShowSuggestions,
    // Premium/Subscription
    onPremiumClick,
    isSubscribed = false,
    networkStatus = 'online',
    // @ts-ignore - kept for backwards compatibility, now handled by HomeSpeedDial
    onHomeReset,
    darkMode,
    onToggleDarkMode,
    highContrast,
    onToggleHighContrast,
    language,
    onToggleLanguage,
    onOpenZiaGivesBack,
    aiSpeedDial,
  } = props;

  // State for road hazard SpeedDial
  const [showHazardMenu, setShowHazardMenu] = useState(false);
  // State for Trash SpeedDial
  const [showTrashMenu, setShowTrashMenu] = useState(false);
  // State for drag-over-trash detection
  const [isDragOverTrash, setIsDragOverTrash] = useState(false);
  // Toast notification for hazard reporting
  const [hazardToast, setHazardToast] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
  
  // 911 Emergency FAB countdown state
  const [emergencyCountdown, setEmergencyCountdown] = useState<number | null>(null);
  const emergencyTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const emergencyStartRef = useRef<number | null>(null);
  const emergencyDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Cop Alert FAB countdown state (3 seconds)
  const [copCountdown, setCopCountdown] = useState<number | null>(null);
  const copTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const copStartRef = useRef<number | null>(null);
  const copDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Safety Guy Alert FAB countdown state (3 seconds)
  const [safetyGuyCountdown, setSafetyGuyCountdown] = useState<number | null>(null);
  const safetyGuyTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const safetyGuyStartRef = useRef<number | null>(null);
  const safetyGuyDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Tron Light Cycle entrance animation state
  const [fabsEntered, setFabsEntered] = useState(false);
  
  // Toolbar visibility state (tap to hide/show)
  const [toolbarVisibility, setToolbarVisibility] = useState<ToolbarVisibility>(DEFAULT_VISIBILITY);
  
  const [layout, setLayout] = useState<ZoneLayout>(DEFAULT_LAYOUT);
  const [draggedFab, setDraggedFab] = useState<string | null>(null);
  const [dragOverZone, setDragOverZone] = useState<FabZone | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [isDragMode, setIsDragMode] = useState(false);
  const [dragPosition, setDragPosition] = useState<{ x: number; y: number } | null>(null);
  
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dragStartPos = useRef<{ x: number; y: number } | null>(null);
  const fabRefs = useRef<Map<string, HTMLElement>>(new Map());
  
  const { accessibleTransitions, tokens } = useM3Motion();
  const cyber = getCyberColors(darkMode, highContrast);

  const arrowAccents = useMemo(() => {
    const themeColors = getCyberColors(darkMode, highContrast);
    const palette = [
      themeColors.neonCyan,
      themeColors.neonGreen,
      themeColors.neonPurple,
      themeColors.neonPink,
      themeColors.neonYellow,
      themeColors.neonBlue,
      themeColors.neonOrange,
      themeColors.neonRed,
    ].filter(Boolean);
    const uniquePalette = Array.from(new Set(palette));
    if (uniquePalette.length === 0) {
      return {
        top: '#38bdf8',
        bottom: '#22c55e',
        left: '#a855f7',
        right: '#f97316',
      };
    }
    const shuffled = [...uniquePalette].sort(() => Math.random() - 0.5);
    const pick = (index: number) => shuffled[index % shuffled.length];
    return {
      top: pick(0),
      bottom: pick(1),
      left: pick(2),
      right: pick(3),
    };
  }, [darkMode, highContrast]);

  // ============================================================================
  // PERSISTENCE
  // ============================================================================

  useEffect(() => {
    const versionKey = 'fab_layout_version';
    const savedVersion = localStorage.getItem(versionKey);
    
    // Reset layout if version changed or doesn't exist
    if (!savedVersion || parseInt(savedVersion) < LAYOUT_VERSION) {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.setItem(versionKey, String(LAYOUT_VERSION));
      setLayout(DEFAULT_LAYOUT);
      return;
    }
    
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved) as ZoneLayout;
        // Ensure left zone exists (for upgrades from v5)
        if (!parsed.left) {
          parsed.left = DEFAULT_LAYOUT.left;
        }
        // Validate layout has required FABs - check for crews (should always be present)
        const allFabs = [...parsed.top, ...(parsed.left || []), ...parsed.right, ...parsed.bottom, ...(parsed.archived || [])];
        if (allFabs.includes('crews') && allFabs.includes('friends')) {
          setLayout(parsed);
        } else {
          // Missing required FABs, reset to default
          localStorage.removeItem(STORAGE_KEY);
          setLayout(DEFAULT_LAYOUT);
        }
      } catch (e) {
        console.error('Failed to parse FAB layout:', e);
        setLayout(DEFAULT_LAYOUT);
      }
    }
  }, []);

  const saveLayout = useCallback((newLayout: ZoneLayout) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newLayout));
    setLayout(newLayout);
  }, []);

  // ============================================================================
  // TOOLBAR VISIBILITY (Tap to Hide/Show)
  // ============================================================================

  // Load toolbar visibility from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(TOOLBAR_VISIBILITY_KEY);
      if (saved) {
        const parsed = JSON.parse(saved) as ToolbarVisibility;
        setToolbarVisibility(parsed);
      }
    } catch (e) {
      console.error('Failed to parse toolbar visibility:', e);
    }
  }, []);

  // Toggle toolbar visibility
  const toggleToolbar = useCallback((zone: FabZone, show?: boolean) => {
    setToolbarVisibility(prev => {
      const newState = { ...prev, [zone]: show !== undefined ? show : !prev[zone] };
      localStorage.setItem(TOOLBAR_VISIBILITY_KEY, JSON.stringify(newState));
      telegram.hapticFeedback('light');
      return newState;
    });
  }, []);

  // Show all toolbars
  const showAllToolbars = useCallback(() => {
    const newState = DEFAULT_VISIBILITY;
    localStorage.setItem(TOOLBAR_VISIBILITY_KEY, JSON.stringify(newState));
    setToolbarVisibility(newState);
    telegram.hapticFeedback('success');
  }, []);

  // Check if any toolbar is hidden
  const anyToolbarHidden = !toolbarVisibility.top || !toolbarVisibility.left || 
                           !toolbarVisibility.right || !toolbarVisibility.bottom;

  // ============================================================================
  // DRAG AND DROP - All FABs freely movable
  // ============================================================================

  // Reset layout to default
  const resetLayout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.setItem('fab_layout_version', String(LAYOUT_VERSION));
    setLayout(DEFAULT_LAYOUT);
    telegram.hapticFeedback('success');
    feedbackService.playSound('success');
  }, []);

  const handleLongPressStart = useCallback((fabId: string, e: React.TouchEvent | React.MouseEvent) => {
    // Store initial position for drag threshold
    const pos = 'touches' in e ? { x: e.touches[0].clientX, y: e.touches[0].clientY } : { x: e.clientX, y: e.clientY };
    dragStartPos.current = pos;
    
    longPressTimer.current = setTimeout(() => {
      setIsDragMode(true);
      setDraggedFab(fabId);
      setDragPosition(pos);
      telegram.hapticFeedback('heavy');
      feedbackService.playSound('select');
    }, LONG_PRESS_DURATION);
  }, []);

  const handleLongPressEnd = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  // Global drag move handler - tracks position anywhere on screen
  const handleGlobalDragMove = useCallback((e: TouchEvent | MouseEvent) => {
    if (!draggedFab || !isDragMode) return;
    
    e.preventDefault(); // Prevent scrolling while dragging
    
    const pos = 'touches' in e ? { x: e.touches[0].clientX, y: e.touches[0].clientY } : { x: e.clientX, y: e.clientY };
    setDragPosition(pos);
    
    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;
    
    // Check if dragging over trash FAB (don't allow archiving 'trash' itself)
    if (draggedFab !== 'trash') {
      const trashEl = fabRefs.current.get('trash');
      if (trashEl) {
        const trashRect = trashEl.getBoundingClientRect();
        const isOverTrash = pos.x >= trashRect.left - 20 && 
                           pos.x <= trashRect.right + 20 && 
                           pos.y >= trashRect.top - 20 && 
                           pos.y <= trashRect.bottom + 20;
        
        if (isOverTrash !== isDragOverTrash) {
          setIsDragOverTrash(isOverTrash);
          if (isOverTrash) {
            telegram.hapticFeedback('heavy');
          }
        }
        
        // If over trash, skip normal zone detection
        if (isOverTrash) {
          setDragOverZone(null);
          setDragOverIndex(null);
          return;
        }
      }
    }
    
    // Reset trash state if not over trash
    if (isDragOverTrash) {
      setIsDragOverTrash(false);
    }
    
    // Determine which zone we're over (4 zones now)
    let zone: FabZone | null = null;
    if (pos.y < 90) {
      zone = 'top';
    } else if (pos.y > windowHeight - 100) {
      zone = 'bottom';
    } else if (pos.x < 100) {
      zone = 'left';
    } else if (pos.x > windowWidth - 100) {
      zone = 'right';
    }
    
    if (zone !== dragOverZone) {
      setDragOverZone(zone);
      setDragOverIndex(null);
      if (zone) {
        telegram.hapticFeedback('light');
      }
    }
    
    // Find which FAB we're hovering over to determine insert position
    if (zone) {
      const zoneFabs = layout[zone];
      let foundIndex: number | null = null;
      
      for (let i = 0; i < zoneFabs.length; i++) {
        const fabId = zoneFabs[i];
        if (fabId === draggedFab) continue;
        
        const el = fabRefs.current.get(fabId);
        if (el) {
          const rect = el.getBoundingClientRect();
          const centerX = rect.left + rect.width / 2;
          const centerY = rect.top + rect.height / 2;
          
          if (zone === 'right' || zone === 'left') {
            // Vertical layout - check Y position
            if (pos.y < centerY) {
              foundIndex = i;
              break;
            }
          } else {
            // Horizontal layout - check X position
            if (pos.x < centerX) {
              foundIndex = i;
              break;
            }
          }
        }
      }
      
      // If no position found, append to end
      if (foundIndex === null) {
        foundIndex = zoneFabs.filter(id => id !== draggedFab).length;
      }
      
      if (foundIndex !== dragOverIndex) {
        setDragOverIndex(foundIndex);
        telegram.hapticFeedback('light');
      }
    }
  }, [draggedFab, isDragMode, dragOverZone, dragOverIndex, layout, isDragOverTrash]);

  // Global drag end handler
  const handleGlobalDragEnd = useCallback(() => {
    handleLongPressEnd();
    
    // Handle dropping on trash (archive the FAB)
    if (draggedFab && isDragOverTrash && draggedFab !== 'trash') {
      const currentZone = Object.entries(layout).find(([zone, ids]) => 
        zone !== 'archived' && (ids as string[]).includes(draggedFab)
      )?.[0] as FabZone | undefined;
      
      if (currentZone) {
        const newLayout = { ...layout };
        
        // Remove from current zone
        newLayout[currentZone] = layout[currentZone].filter(id => id !== draggedFab);
        
        // Add to archived
        newLayout.archived = [...(layout.archived || []), draggedFab];
        
        saveLayout(newLayout);
        telegram.hapticFeedback('success');
        feedbackService.playSound('success');
        setHazardToast({ open: true, message: `🗑️ Archived`, severity: 'success' });
      }
    }
    // Handle normal zone drop
    else if (draggedFab && dragOverZone !== null) {
      const currentZone = Object.entries(layout).find(([zone, ids]) => 
        zone !== 'archived' && (ids as string[]).includes(draggedFab)
      )?.[0] as FabZone | undefined;
      
      if (currentZone) {
        const newLayout = { ...layout };
        
        // Remove from current zone
        newLayout[currentZone] = layout[currentZone].filter(id => id !== draggedFab);
        
        // Insert at specific position in target zone
        const insertIndex = dragOverIndex !== null ? dragOverIndex : newLayout[dragOverZone].length;
        newLayout[dragOverZone] = [
          ...newLayout[dragOverZone].slice(0, insertIndex),
          draggedFab,
          ...newLayout[dragOverZone].slice(insertIndex)
        ];
        
        saveLayout(newLayout);
        telegram.hapticFeedback('success');
        feedbackService.playSound('success');
      }
    }
    
    setDraggedFab(null);
    setDragOverZone(null);
    setDragOverIndex(null);
    setDragPosition(null);
    setIsDragMode(false);
    setIsDragOverTrash(false);
    dragStartPos.current = null;
  }, [draggedFab, dragOverZone, dragOverIndex, layout, saveLayout, handleLongPressEnd, isDragOverTrash]);

  // Attach global event listeners when dragging
  useEffect(() => {
    if (isDragMode && draggedFab) {
      document.addEventListener('touchmove', handleGlobalDragMove, { passive: false });
      document.addEventListener('mousemove', handleGlobalDragMove);
      document.addEventListener('touchend', handleGlobalDragEnd);
      document.addEventListener('mouseup', handleGlobalDragEnd);
      
      return () => {
        document.removeEventListener('touchmove', handleGlobalDragMove);
        document.removeEventListener('mousemove', handleGlobalDragMove);
        document.removeEventListener('touchend', handleGlobalDragEnd);
        document.removeEventListener('mouseup', handleGlobalDragEnd);
      };
    }
  }, [isDragMode, draggedFab, handleGlobalDragMove, handleGlobalDragEnd]);

  // ============================================================================
  // TRON LIGHT CYCLE ENTRANCE ANIMATION
  // ============================================================================

  useEffect(() => {
    if (visible && !fabsEntered) {
      // Stagger the entrance animation
      const timer = setTimeout(() => {
        setFabsEntered(true);
      }, 100);
      return () => clearTimeout(timer);
    }
    // Reset when hidden
    if (!visible && fabsEntered) {
      setFabsEntered(false);
    }
  }, [visible, fabsEntered]);

  // ============================================================================
  // 911 EMERGENCY HANDLERS
  // ============================================================================

  const handleEmergencyStart = useCallback(async () => {
    // Wait 600ms before starting countdown to avoid conflict with drag-and-drop (500ms)
    emergencyDelayRef.current = setTimeout(() => {
      // Don't start if drag mode was activated
      if (isDragMode) return;
      
      // Start 5-second countdown
      emergencyStartRef.current = Date.now();
      setEmergencyCountdown(EMERGENCY_COUNTDOWN_SECONDS);
      telegram.hapticFeedback('heavy');
      
      // Start countdown interval
      emergencyTimerRef.current = setInterval(() => {
        const elapsed = (Date.now() - (emergencyStartRef.current || 0)) / 1000;
        const remaining = Math.ceil(EMERGENCY_COUNTDOWN_SECONDS - elapsed);
        
        if (remaining <= 0) {
          // Countdown complete - make the call!
          clearInterval(emergencyTimerRef.current!);
          emergencyTimerRef.current = null;
          setEmergencyCountdown(null);
          
          // Make the emergency call
          telegram.hapticFeedback('success');
          feedbackService.playSound('success');
          window.location.href = 'tel:911';
        } else {
          setEmergencyCountdown(remaining);
          telegram.hapticFeedback('medium');
        }
      }, 1000);
    }, 600);
  }, [isDragMode]);

  const handleEmergencyEnd = useCallback(() => {
    // Cancel delay timer if not yet started
    if (emergencyDelayRef.current) {
      clearTimeout(emergencyDelayRef.current);
      emergencyDelayRef.current = null;
    }
    // Cancel countdown if released early
    if (emergencyTimerRef.current) {
      clearInterval(emergencyTimerRef.current);
      emergencyTimerRef.current = null;
    }
    emergencyStartRef.current = null;
    setEmergencyCountdown(null);
  }, []);

  // Cop Alert countdown handlers
  const handleCopAlertStart = useCallback(() => {
    // Wait 600ms before starting countdown to avoid conflict with drag-and-drop (500ms)
    copDelayRef.current = setTimeout(() => {
      // Don't start if drag mode was activated
      if (isDragMode) return;
      
      // Start 3-second countdown
      copStartRef.current = Date.now();
      setCopCountdown(COP_COUNTDOWN_SECONDS);
      telegram.hapticFeedback('heavy');
      
      // Start countdown interval
      copTimerRef.current = setInterval(() => {
        const elapsed = (Date.now() - (copStartRef.current || 0)) / 1000;
        const remaining = Math.ceil(COP_COUNTDOWN_SECONDS - elapsed);
        
        if (remaining <= 0) {
          // Countdown complete - report the cop!
          clearInterval(copTimerRef.current!);
          copTimerRef.current = null;
          setCopCountdown(null);
          
          // Call the cop alert handler
          if (onCopAlert) {
            telegram.hapticFeedback('success');
            feedbackService.playSound('success');
            onCopAlert();
          }
        } else {
          setCopCountdown(remaining);
          telegram.hapticFeedback('medium');
        }
      }, 1000);
    }, 600);
  }, [onCopAlert, isDragMode]);

  const handleCopAlertEnd = useCallback(() => {
    // Cancel delay timer if not yet started
    if (copDelayRef.current) {
      clearTimeout(copDelayRef.current);
      copDelayRef.current = null;
    }
    // Cancel countdown if released early
    if (copTimerRef.current) {
      clearInterval(copTimerRef.current);
      copTimerRef.current = null;
    }
    copStartRef.current = null;
    setCopCountdown(null);
  }, []);

  // Safety Guy Alert countdown handlers
  const handleSafetyGuyStart = useCallback(() => {
    // Wait 600ms before starting countdown to avoid conflict with drag-and-drop (500ms)
    safetyGuyDelayRef.current = setTimeout(() => {
      // Don't start if drag mode was activated
      if (isDragMode) return;
      
      // Start 3-second countdown
      safetyGuyStartRef.current = Date.now();
      setSafetyGuyCountdown(COP_COUNTDOWN_SECONDS);
      telegram.hapticFeedback('heavy');
      
      // Start countdown interval
      safetyGuyTimerRef.current = setInterval(() => {
        const elapsed = (Date.now() - (safetyGuyStartRef.current || 0)) / 1000;
        const remaining = Math.ceil(COP_COUNTDOWN_SECONDS - elapsed);
        
        if (remaining <= 0) {
          // Countdown complete - report the safety guy!
          clearInterval(safetyGuyTimerRef.current!);
          safetyGuyTimerRef.current = null;
          setSafetyGuyCountdown(null);
          
          // Call the safety guy handler
          if (onReportSafetyGuy) {
            telegram.hapticFeedback('success');
            feedbackService.playSound('success');
            onReportSafetyGuy();
          }
        } else {
          setSafetyGuyCountdown(remaining);
          telegram.hapticFeedback('medium');
        }
      }, 1000);
    }, 600);
  }, [onReportSafetyGuy, isDragMode]);

  const handleSafetyGuyEnd = useCallback(() => {
    // Cancel delay timer if not yet started
    if (safetyGuyDelayRef.current) {
      clearTimeout(safetyGuyDelayRef.current);
      safetyGuyDelayRef.current = null;
    }
    // Cancel countdown if released early
    if (safetyGuyTimerRef.current) {
      clearInterval(safetyGuyTimerRef.current);
      safetyGuyTimerRef.current = null;
    }
    safetyGuyStartRef.current = null;
    setSafetyGuyCountdown(null);
  }, []);

  // Cleanup emergency and cop timers on unmount
  useEffect(() => {
    return () => {
      if (emergencyDelayRef.current) {
        clearTimeout(emergencyDelayRef.current);
      }
      if (emergencyTimerRef.current) {
        clearInterval(emergencyTimerRef.current);
      }
      if (copDelayRef.current) {
        clearTimeout(copDelayRef.current);
      }
      if (copTimerRef.current) {
        clearInterval(copTimerRef.current);
      }
    };
  }, []);

  // ============================================================================
  // FAB RENDERERS
  // ============================================================================

  const renderFab = (fabId: string, zone: FabZone, index: number) => {
    const isBeingDragged = draggedFab === fabId;
    const isDropTarget = dragOverZone === zone && dragOverIndex === index && draggedFab !== fabId;
    
    // Tron Light Cycle entrance animation - FABs slide in from their zone edge
    const entranceDelay = index * 50; // Stagger each FAB by 50ms
    const getEntranceTransform = () => {
      if (fabsEntered) return 'translate(0, 0)';
      switch (zone) {
        case 'top': return 'translate(0, -100px)';
        case 'bottom': return 'translate(0, 100px)';
        case 'left': return 'translate(-100px, 0)';
        case 'right': return 'translate(100px, 0)';
      }
    };
    
    // Common FAB styling - all FABs draggable
    const fabSx = {
      width: FAB_SIZE,
      height: FAB_SIZE,
      opacity: isBeingDragged ? 0.3 : 1,
      transform: isBeingDragged ? 'scale(0.8)' : 'scale(1)',
      transition: accessibleTransitions.fab,
      cursor: isDragMode ? 'grab' : 'pointer',
    };
    
    // Wrapper with ref for position tracking and drop indicator
    const wrapperSx = {
      position: 'relative' as const,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0, // Prevent FAB compression in scrollable containers
      // Tron Light Cycle entrance animation
      transform: getEntranceTransform(),
      opacity: fabsEntered ? 1 : 0,
      transition: `transform 400ms cubic-bezier(0.22, 1, 0.36, 1) ${entranceDelay}ms, opacity 300ms ease ${entranceDelay}ms`,
      // Neon glow trail effect during entrance
      ...((!fabsEntered) && {
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: -4,
          borderRadius: '50%',
          background: zone === 'top' || zone === 'bottom' 
            ? `linear-gradient(${zone === 'top' ? '180deg' : '0deg'}, ${cyber.neonCyan}60, transparent 80%)`
            : `linear-gradient(${zone === 'left' ? '90deg' : '270deg'}, ${cyber.neonCyan}60, transparent 80%)`,
          filter: `blur(8px)`,
          opacity: 0.8,
          transition: 'opacity 200ms ease',
        },
      }),
    };
    
    // Drop indicator style - position based on zone orientation
    const dropIndicatorStyle = isDropTarget ? {
      '&::before': {
        content: '""',
        position: 'absolute',
        ...((zone === 'right' || zone === 'left')
          ? { top: -8, left: '50%', transform: 'translateX(-50%)', width: 40, height: 4 }
          : { left: -8, top: '50%', transform: 'translateY(-50%)', width: 4, height: 40 }
        ),
        background: cyber.neonCyan,
        borderRadius: 2,
        boxShadow: `0 0 10px ${cyber.neonCyan}`,
        animation: 'pulse 0.5s ease-in-out infinite',
      }
    } : {};

    // Handle special FABs that are passed as components
    if (fabId === 'ai') {
      return (
        <Box 
          key={fabId}
          ref={(el: HTMLElement | null) => { if (el) fabRefs.current.set(fabId, el); }}
          sx={{ ...wrapperSx, ...dropIndicatorStyle }}
        >
          <Box 
            sx={{ 
              ...fabSx,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onTouchStart={(e) => handleLongPressStart(fabId, e)}
            onTouchEnd={handleLongPressEnd}
            onMouseDown={(e) => handleLongPressStart(fabId, e)}
            onMouseUp={handleLongPressEnd}
            onMouseLeave={handleLongPressEnd}
          >
            {/* AI is a dialog-based FAB, zone not needed */}
            {aiSpeedDial}
          </Box>
        </Box>
      );
    }

    // Emergency 911 FAB - special handling for press-and-hold countdown
    if (fabId === 'emergency911') {
      const emergencyConfig = {
        icon: emergencyCountdown !== null ? (
          <Typography sx={{
            fontSize: 28,
            fontWeight: 900,
            color: '#ffffff',
            textShadow: '0 0 10px #dc2626, 0 0 20px #dc2626',
            animation: 'emergency-pulse 0.5s ease-in-out infinite',
            '@keyframes emergency-pulse': {
              '0%, 100%': { transform: 'scale(1)', opacity: 1 },
              '50%': { transform: 'scale(1.2)', opacity: 0.8 },
            },
          }}>
            {emergencyCountdown}
          </Typography>
        ) : (
          <LocalHospitalIcon sx={{ 
            fontSize: ICON_SIZE, 
            color: '#ffffff',
            filter: 'drop-shadow(0 0 6px #dc2626)',
          }} />
        ),
        color: emergencyCountdown !== null ? '#991b1b' : '#dc2626',
        label: emergencyCountdown !== null 
          ? `🆘 ${emergencyCountdown}... RELEASE TO CANCEL` 
          : '🆘 HOLD 5s to Call 911',
      };

      return (
        <Box 
          key={fabId}
          ref={(el: HTMLElement | null) => { if (el) fabRefs.current.set(fabId, el); }}
          sx={{ ...wrapperSx, ...dropIndicatorStyle }}
        >
          <Tooltip 
            title={emergencyConfig.label} 
            placement={
              zone === 'bottom' ? 'top' : 
              zone === 'top' ? 'bottom' : 
              zone === 'left' ? 'right' : 'left'
            }
          >
            <IconButton
              onTouchStart={(e) => {
                if (!isDragMode) handleEmergencyStart();
                handleLongPressStart(fabId, e);
              }}
              onTouchEnd={() => {
                handleEmergencyEnd();
                handleLongPressEnd();
              }}
              onMouseDown={(e) => {
                if (!isDragMode) handleEmergencyStart();
                handleLongPressStart(fabId, e);
              }}
              onMouseUp={() => {
                handleEmergencyEnd();
                handleLongPressEnd();
              }}
              onMouseLeave={() => {
                handleEmergencyEnd();
                handleLongPressEnd();
              }}
              sx={{
                ...fabSx,
                background: emergencyCountdown !== null 
                  ? `linear-gradient(135deg, #991b1b 0%, #dc2626 100%)`
                  : `linear-gradient(135deg, ${emergencyConfig.color}30 0%, ${emergencyConfig.color}50 100%)`,
                backdropFilter: 'blur(12px)',
                border: emergencyCountdown !== null 
                  ? `${FAB_BORDER_WIDTH}px solid #fef2f2`
                  : isDragMode 
                    ? `${FAB_BORDER_WIDTH}px dashed ${cyber.neonPurple}` 
                    : `${FAB_BORDER_WIDTH}px solid ${emergencyConfig.color}90`,
                boxShadow: emergencyCountdown !== null 
                  ? `0 0 30px #dc2626, 0 0 60px #dc262680, inset 0 0 20px #dc262640`
                  : `0 4px 20px ${emergencyConfig.color}60, inset 0 0 15px ${emergencyConfig.color}20`,
                animation: emergencyCountdown !== null ? 'emergency-fab-pulse 0.3s ease-in-out infinite' : 'none',
                '@keyframes emergency-fab-pulse': {
                  '0%, 100%': { transform: 'scale(1)' },
                  '50%': { transform: 'scale(1.05)' },
                },
                '&:hover': {
                  transform: 'scale(1.08)',
                  boxShadow: `0 6px 25px ${emergencyConfig.color}70, inset 0 0 20px ${emergencyConfig.color}30`,
                  border: `${FAB_BORDER_WIDTH}px solid ${emergencyConfig.color}`,
                },
                '&:active': {
                  transform: 'scale(0.95)',
                },
              }}
            >
              {emergencyConfig.icon}
            </IconButton>
          </Tooltip>
        </Box>
      );
    }

    // Cop Alert FAB - special handling for press-and-hold countdown (3 seconds)
    if (fabId === 'copAlert') {
      const copConfig = {
        icon: copCountdown !== null ? (
          <Typography sx={{
            fontSize: 28,
            fontWeight: 900,
            color: '#ffffff',
            textShadow: '0 0 10px #3b82f6, 0 0 20px #3b82f6',
            animation: 'cop-pulse 0.5s ease-in-out infinite',
            '@keyframes cop-pulse': {
              '0%, 100%': { transform: 'scale(1)', opacity: 1 },
              '50%': { transform: 'scale(1.2)', opacity: 0.8 },
            },
          }}>
            {copCountdown}
          </Typography>
        ) : (
          <LocalPoliceIcon sx={{ 
            fontSize: ICON_SIZE, 
            color: '#ffffff',
            filter: 'drop-shadow(0 0 6px #3b82f6)',
          }} />
        ),
        color: copCountdown !== null ? '#1e40af' : '#3b82f6', // Darker blue during countdown
        label: copCountdown !== null 
          ? `🚨 ${copCountdown}... RELEASE TO CANCEL` 
          : '🚨 HOLD 3s to Report Cop',
      };

      return (
        <Box 
          key={fabId}
          ref={(el: HTMLElement | null) => { if (el) fabRefs.current.set(fabId, el); }}
          sx={{ ...wrapperSx, ...dropIndicatorStyle }}
        >
          <Tooltip 
            title={copConfig.label} 
            placement={
              zone === 'bottom' ? 'top' : 
              zone === 'top' ? 'bottom' : 
              zone === 'left' ? 'right' : 'left'
            }
          >
            <IconButton
              onTouchStart={(e) => {
                if (!isDragMode) handleCopAlertStart();
                handleLongPressStart(fabId, e);
              }}
              onTouchEnd={() => {
                handleCopAlertEnd();
                handleLongPressEnd();
              }}
              onMouseDown={(e) => {
                if (!isDragMode) handleCopAlertStart();
                handleLongPressStart(fabId, e);
              }}
              onMouseUp={() => {
                handleCopAlertEnd();
                handleLongPressEnd();
              }}
              onMouseLeave={() => {
                handleCopAlertEnd();
                handleLongPressEnd();
              }}
              sx={{
                ...fabSx,
                background: copCountdown !== null 
                  ? `linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)`
                  : `linear-gradient(135deg, ${copConfig.color}30 0%, ${copConfig.color}50 100%)`,
                backdropFilter: 'blur(12px)',
                border: copCountdown !== null 
                  ? `${FAB_BORDER_WIDTH}px solid #dbeafe`
                  : isDragMode 
                    ? `${FAB_BORDER_WIDTH}px dashed ${cyber.neonPurple}` 
                    : `${FAB_BORDER_WIDTH}px solid ${copConfig.color}90`,
                boxShadow: copCountdown !== null 
                  ? `0 0 30px #3b82f6, 0 0 60px #3b82f680, inset 0 0 20px #3b82f640`
                  : `0 4px 20px ${copConfig.color}60, inset 0 0 15px ${copConfig.color}20`,
                animation: copCountdown !== null ? 'cop-fab-pulse 0.3s ease-in-out infinite' : 'none',
                '@keyframes cop-fab-pulse': {
                  '0%, 100%': { transform: 'scale(1)' },
                  '50%': { transform: 'scale(1.05)' },
                },
                '&:hover': {
                  transform: 'scale(1.08)',
                  boxShadow: `0 6px 25px ${copConfig.color}70, inset 0 0 20px ${copConfig.color}30`,
                  border: `${FAB_BORDER_WIDTH}px solid ${copConfig.color}`,
                },
                '&:active': {
                  transform: 'scale(0.95)',
                },
              }}
            >
              {copConfig.icon}
            </IconButton>
          </Tooltip>
        </Box>
      );
    }

    // Safety Guy Alert FAB - special handling for press-and-hold countdown (3 seconds)
    if (fabId === 'safetyGuy') {
      const safetyGuyConfig = {
        icon: safetyGuyCountdown !== null ? (
          <Typography sx={{
            fontSize: 28,
            fontWeight: 900,
            color: '#ffffff',
            textShadow: '0 0 10px #ec4899, 0 0 20px #ec4899',
            animation: 'safety-pulse 0.5s ease-in-out infinite',
            '@keyframes safety-pulse': {
              '0%, 100%': { transform: 'scale(1)', opacity: 1 },
              '50%': { transform: 'scale(1.2)', opacity: 0.8 },
            },
          }}>
            {safetyGuyCountdown}
          </Typography>
        ) : (
          <LocalPoliceIcon sx={{ 
            fontSize: ICON_SIZE, 
            color: '#ffffff',
            filter: 'drop-shadow(0 0 6px #ec4899)',
          }} />
        ),
        color: safetyGuyCountdown !== null ? '#be185d' : '#ec4899', // Darker pink during countdown
        label: safetyGuyCountdown !== null 
          ? `👷 ${safetyGuyCountdown}... RELEASE TO CANCEL` 
          : '👷 HOLD 3s to Report Safety Inspector',
      };

      return (
        <Box 
          key={fabId}
          ref={(el: HTMLElement | null) => { if (el) fabRefs.current.set(fabId, el); }}
          sx={{ ...wrapperSx, ...dropIndicatorStyle }}
        >
          <Tooltip 
            title={safetyGuyConfig.label} 
            placement={
              zone === 'bottom' ? 'top' : 
              zone === 'top' ? 'bottom' : 
              zone === 'left' ? 'right' : 'left'
            }
          >
            <IconButton
              onTouchStart={(e) => {
                if (!isDragMode) handleSafetyGuyStart();
                handleLongPressStart(fabId, e);
              }}
              onTouchEnd={() => {
                handleSafetyGuyEnd();
                handleLongPressEnd();
              }}
              onMouseDown={(e) => {
                if (!isDragMode) handleSafetyGuyStart();
                handleLongPressStart(fabId, e);
              }}
              onMouseUp={() => {
                handleSafetyGuyEnd();
                handleLongPressEnd();
              }}
              onMouseLeave={() => {
                handleSafetyGuyEnd();
                handleLongPressEnd();
              }}
              sx={{
                ...fabSx,
                background: safetyGuyCountdown !== null 
                  ? `linear-gradient(135deg, #be185d 0%, #ec4899 100%)`
                  : `linear-gradient(135deg, ${safetyGuyConfig.color}30 0%, ${safetyGuyConfig.color}50 100%)`,
                backdropFilter: 'blur(12px)',
                border: safetyGuyCountdown !== null 
                  ? `${FAB_BORDER_WIDTH}px solid #fbcfe8`
                  : isDragMode 
                    ? `${FAB_BORDER_WIDTH}px dashed ${cyber.neonPurple}` 
                    : `${FAB_BORDER_WIDTH}px solid ${safetyGuyConfig.color}90`,
                boxShadow: safetyGuyCountdown !== null 
                  ? `0 0 30px #ec4899, 0 0 60px #ec489980, inset 0 0 20px #ec489940`
                  : `0 4px 20px ${safetyGuyConfig.color}60, inset 0 0 15px ${safetyGuyConfig.color}20`,
                animation: safetyGuyCountdown !== null ? 'safety-fab-pulse 0.3s ease-in-out infinite' : 'none',
                '@keyframes safety-fab-pulse': {
                  '0%, 100%': { transform: 'scale(1)' },
                  '50%': { transform: 'scale(1.05)' },
                },
                '&:hover': {
                  transform: 'scale(1.08)',
                  boxShadow: `0 6px 25px ${safetyGuyConfig.color}70, inset 0 0 20px ${safetyGuyConfig.color}30`,
                  border: `${FAB_BORDER_WIDTH}px solid ${safetyGuyConfig.color}`,
                },
                '&:active': {
                  transform: 'scale(0.95)',
                },
              }}
            >
              {safetyGuyConfig.icon}
            </IconButton>
          </Tooltip>
        </Box>
      );
    }

    // Get FAB config based on ID - each FAB has a UNIQUE color for visibility
    // In light mode, all icons should be dark for visibility
    const iconDark = '#0f172a'; // Dark slate for light mode
    const iconLight = '#fef3c7'; // Cream for dark mode
    const iconColor = darkMode ? iconLight : iconDark;
    const iconGlow = darkMode ? 'drop-shadow(0 0 3px rgba(254,243,199,0.8))' : 'drop-shadow(0 0 3px rgba(15,23,42,0.8))';
    
    const getFabConfig = (): { icon: ReactNode; color: string; onClick: () => void; label: string; badge?: number } | null => {
      switch (fabId) {
        case 'hamburger':
          return {
            icon: <MenuIcon sx={{ fontSize: ICON_SIZE, color: iconColor, filter: iconGlow }} />,
            color: cyber.neonCyan, // Ring: Cyan
            onClick: onOpenDrawer,
            label: 'Menu',
          };
        case 'settings':
          return {
            icon: <SettingsIcon sx={{ 
              fontSize: ICON_SIZE - 4, 
              color: iconColor,
              filter: iconGlow,
              transform: showSettingsPopout ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: `transform ${tokens.duration.medium2}ms ${tokens.easing.emphasized}`,
            }} />,
            color: '#8b5cf6', // Ring: Violet-500
            onClick: onShowSettings,
            label: 'Settings',
          };
        case 'favorites':
          return {
            icon: favoritesCount > 0 
              ? <StarIcon sx={{ fontSize: ICON_SIZE, color: iconColor, filter: iconGlow }} />
              : <StarBorderIcon sx={{ fontSize: ICON_SIZE, color: iconColor, filter: iconGlow }} />,
            color: cyber.neonPurple, // Ring: Purple
            onClick: onFavoritesClick,
            label: 'Favorites',
            badge: favoritesCount > 0 ? favoritesCount : undefined,
          };
        case 'voice':
          return {
            icon: <RecordVoiceOverIcon sx={{ 
              fontSize: ICON_SIZE, 
              color: iconColor,
              filter: voiceStatus === 'listening' 
                ? 'drop-shadow(0 0 4px rgba(15,23,42,0.9))' 
                : iconGlow,
            }} />,
            color: voiceStatus === 'listening' ? cyber.neonGreen : '#22d3ee', // Ring: Green/Cyan
            onClick: onVoiceClick,
            label: 'Voice Search (iOS)',
          };
        case 'search':
          return {
            icon: <LocalGasStationIcon sx={{ fontSize: ICON_SIZE, color: iconColor, filter: iconGlow }} />,
            color: '#fb923c', // Ring: Orange
            onClick: onSearchClick,
            label: 'Facilities',
          };
        case 'manualSearch':
          return {
            icon: <SearchIcon sx={{ fontSize: ICON_SIZE, color: iconColor, filter: iconGlow }} />,
            color: '#78716c', // Ring: Stone
            onClick: onManualSearchClick,
            label: 'Search Facilities',
          };
        case 'map':
          return {
            icon: <MapIcon sx={{ fontSize: ICON_SIZE, color: iconColor, filter: iconGlow }} />,
            color: cyber.neonGold, // Ring: Gold
            onClick: onMapClick,
            label: 'View on Map',
          };
        case 'crews':
          return {
            icon: <GroupIcon sx={{ fontSize: ICON_SIZE, color: iconColor, filter: iconGlow }} />,
            color: '#38bdf8', // Ring: Sky Blue
            onClick: onCrewsClick,
            label: 'My Crews',
          };
        // case 'routes':
        //   return {
        //     icon: <RouteIcon sx={{ fontSize: ICON_SIZE, color: iconColor, filter: iconGlow }} />,
        //     color: '#60a5fa', // Ring: Blue
        //     onClick: onRoutesClick,
        //     label: 'Route Photos',
        //   };
        case 'calicheRoutes':
          return {
            icon: <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <RouteIcon sx={{ fontSize: ICON_SIZE, color: iconColor, filter: iconGlow }} />
              <Typography sx={{ 
                position: 'absolute', 
                bottom: -4, 
                fontSize: 10, 
                fontWeight: 900,
                color: '#f97316',
                textShadow: '0 0 4px rgba(249, 115, 22, 0.8)',
              }}>🛣️</Typography>
            </Box>,
            color: '#f97316', // Ring: Orange
            onClick: onCalicheRoutesClick || (() => {}),
            label: 'Caliche Routes',
          };
        case 'facilitiesView':
          return {
            icon: <BusinessIcon sx={{ fontSize: ICON_SIZE, color: iconColor, filter: iconGlow }} />,
            color: '#06b6d4', // Ring: Cyan
            onClick: onFacilitiesViewClick || (() => {}),
            label: 'Facilities',
          };
        case 'hazardsView':
          return {
            icon: <WarningIcon sx={{ fontSize: ICON_SIZE, color: iconColor, filter: iconGlow }} />,
            color: '#f59e0b', // Ring: Amber
            onClick: onHazardsViewClick || (() => {}),
            label: 'View Hazards',
          };
        case 'activity':
          return {
            icon: <TrendingUpIcon sx={{ fontSize: ICON_SIZE, color: iconColor, filter: iconGlow }} />,
            color: '#ec4899', // Ring: Pink
            onClick: onActivityClick,
            label: 'Active Operations',
          };
        case 'services':
          return {
            icon: <BuildIcon sx={{ fontSize: ICON_SIZE, color: iconColor, filter: iconGlow }} />,
            color: '#84cc16', // Ring: Lime
            onClick: onServicesClick,
            label: 'Find Services',
          };
        case 'jobs':
          return {
            icon: <WorkIcon sx={{ fontSize: ICON_SIZE, color: iconColor, filter: iconGlow }} />,
            color: '#f43f5e', // Ring: Rose
            onClick: onJobsClick,
            label: 'Jobs Board',
          };
        case 'friends':
          return {
            icon: <PeopleIcon sx={{ fontSize: ICON_SIZE, color: iconColor, filter: iconGlow }} />,
            color: '#d946ef', // Ring: Fuchsia
            onClick: onFriendsClick,
            label: 'Friends & Amigos',
          };
        case 'basin-community':
          return {
            icon: <GroupsIcon sx={{ fontSize: ICON_SIZE, color: iconColor, filter: iconGlow }} />,
            color: '#14b8a6', // Ring: Teal
            onClick: onBasinCommunityClick || (() => {}),
            label: 'Basin Community',
          };
        case 'copAlert':
          // Cop Alert - Report police location to ALL users (flashing on map)
          return {
            icon: <LocalPoliceIcon sx={{ 
              fontSize: ICON_SIZE, 
              color: iconColor,
              filter: iconGlow,
            }} />,
            color: '#3b82f6', // Ring: Blue
            onClick: onCopAlert || (() => {}),
            label: '🚨 Report Cop Location',
          };
        case 'safetyGuy':
          // Safety Guy Alert - Report safety inspector location to ALL users (flashing on map)
          return {
            icon: <LocalPoliceIcon sx={{ 
              fontSize: ICON_SIZE, 
              color: iconColor,
              filter: iconGlow,
            }} />,
            color: '#ec4899', // Ring: Pink
            onClick: onReportSafetyGuy || (() => {}),
            label: '👷 Report Safety Inspector',
          };
        case 'emergency911':
          // 911 Emergency - Hold for 5 seconds to call
          return {
            icon: emergencyCountdown !== null ? (
              // Show countdown number during hold
              <Typography sx={{
                fontSize: 28,
                fontWeight: 900,
                color: '#ffffff',
                textShadow: '0 0 10px #dc2626, 0 0 20px #dc2626',
                animation: 'emergency-pulse 0.5s ease-in-out infinite',
                '@keyframes emergency-pulse': {
                  '0%, 100%': { transform: 'scale(1)', opacity: 1 },
                  '50%': { transform: 'scale(1.2)', opacity: 0.8 },
                },
              }}>
                {emergencyCountdown}
              </Typography>
            ) : (
              <LocalHospitalIcon sx={{ 
                fontSize: ICON_SIZE, 
                color: '#ffffff',
                filter: 'drop-shadow(0 0 6px #dc2626)',
              }} />
            ),
            color: emergencyCountdown !== null ? '#991b1b' : '#dc2626', // Darker red during countdown
            onClick: () => {}, // Handled by touch/mouse events
            label: emergencyCountdown !== null 
              ? `🆘 ${emergencyCountdown}... RELEASE TO CANCEL` 
              : '🆘 HOLD 5s to Call 911',
          };
        case 'communityFacilities':
          return {
            icon: communityFacilitiesCount && communityFacilitiesCount > 0 ? (
              <Badge
                badgeContent={communityFacilitiesCount}
                color="primary"
                max={999}
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.6rem',
                    minWidth: 14,
                    height: 14,
                    fontWeight: 700,
                  }
                }}
              >
                <GroupsIcon sx={{ fontSize: ICON_SIZE, color: iconColor, filter: iconGlow }} />
              </Badge>
            ) : <GroupsIcon sx={{ fontSize: ICON_SIZE, color: iconColor, filter: iconGlow }} />,
            color: '#22d3ee', // Ring: Cyan
            onClick: onCommunityFacilitiesClick || (() => {}),
            label: `Community Facilities${communityFacilitiesCount ? ` (${communityFacilitiesCount})` : ''}`,
            badge: communityFacilitiesCount,
          };
        case 'qrShare':
          return {
            icon: <QrCode2Icon sx={{ fontSize: ICON_SIZE, color: iconColor, filter: iconGlow }} />,
            color: '#3b82f6', // Ring: Blue
            onClick: onShowQrCode || (() => {}),
            label: 'Share QR Code',
          };
        case 'suggestions':
          return {
            icon: <FeedbackIcon sx={{ fontSize: ICON_SIZE, color: iconColor, filter: iconGlow }} />,
            color: '#10b981', // Ring: Emerald
            onClick: onShowSuggestions || (() => {}),
            label: 'Suggest a Feature',
          };
        case 'premium':
          return {
            icon: isSubscribed 
              ? <WorkspacePremiumIcon sx={{ fontSize: ICON_SIZE, color: '#fbbf24', filter: iconGlow }} />
              : <WorkspacePremiumIcon sx={{ 
                  fontSize: ICON_SIZE, 
                  color: '#fbbf24', 
                  filter: iconGlow,
                  animation: 'pulse 2s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%, 100%': { transform: 'scale(1)', opacity: 1 },
                    '50%': { transform: 'scale(1.15)', opacity: 0.8 },
                  }
                }} />,
            color: isSubscribed ? '#fbbf24' : '#8b5cf6', // Gold if subscribed, Purple otherwise
            onClick: onPremiumClick,
            label: isSubscribed ? 'Premium Active 🏆' : '🏆 Get Premium - Unlock All Features!',
          };
        // ========== ZIA GIVES BACK - CHARITY/GIVING FEATURE ==========
        case 'ziaGivesBack':
          return {
            icon: <FavoriteIcon sx={{ fontSize: ICON_SIZE, color: '#f472b6' }} />,
            color: '#f472b6',
            onClick: () => {
              telegram.hapticFeedback('medium');
              onOpenZiaGivesBack?.();
            },
            label: 'Zia Gives Back - Help Our Community',
          };
        case 'support':
          return {
            icon: <SupportAgentIcon sx={{ fontSize: ICON_SIZE, color: iconColor, filter: iconGlow }} />,
            color: '#6366f1', // Ring: Indigo
            onClick: () => {
              // Open paid Telegram support channel
              const tg = (window as any).Telegram?.WebApp;
              const SUPPORT_URL = 'https://t.me/ziawellnavsupportbot';
              if (tg?.openTelegramLink) {
                tg.openTelegramLink(SUPPORT_URL);
              } else if (tg?.openLink) {
                tg.openLink(SUPPORT_URL, { try_instant_view: false });
              } else {
                window.open(SUPPORT_URL, '_blank');
              }
            },
            label: 'Contact Support ($1/msg)',
          };
        case 'networkStatus':
          return {
            icon: networkStatus === 'online' 
              ? <SignalWifi4BarIcon sx={{ fontSize: ICON_SIZE - 4, color: iconColor, filter: iconGlow }} />
              : networkStatus === 'poor'
              ? <WifiIcon sx={{ fontSize: ICON_SIZE - 4, color: iconColor, filter: iconGlow }} />
              : <WifiOffIcon sx={{ fontSize: ICON_SIZE - 4, color: iconColor, filter: iconGlow }} />,
            color: networkStatus === 'online' ? cyber.neonGreen : networkStatus === 'poor' ? '#fbbf24' : cyber.neonRed,
            onClick: () => { /* Just a status indicator */ },
            label: networkStatus === 'online' ? 'Online' : networkStatus === 'poor' ? 'Poor Signal' : 'Offline',
          };
        case 'trash': {
          const archivedCount = layout.archived?.length || 0;
          return {
            icon: isDragOverTrash 
              ? <DeleteIcon sx={{ fontSize: ICON_SIZE, color: iconColor, filter: iconGlow, transform: 'scale(1.2)' }} />
              : archivedCount > 0
              ? <DeleteIcon sx={{ fontSize: ICON_SIZE, color: iconColor, filter: iconGlow }} />
              : <DeleteOutlineIcon sx={{ fontSize: ICON_SIZE, color: iconColor, filter: iconGlow }} />,
            color: isDragOverTrash ? '#ef4444' : archivedCount > 0 ? '#f59e0b' : '#06b6d4', // Ring colors stay same
            onClick: () => {
              if (archivedCount > 0) {
                setShowTrashMenu(!showTrashMenu);
              } else {
                telegram.hapticFeedback('light');
              }
            },
            label: archivedCount > 0 ? `Trash (${archivedCount})` : 'Trash (empty)',
            badge: archivedCount > 0 ? archivedCount : undefined,
          };
        }
        default:
          return null;
      }
    };

    const config = getFabConfig();
    if (!config) return null;

    const handleClick = (e: React.MouseEvent) => {
      // Stop propagation to prevent toolbar from hiding when clicking a FAB
      e.stopPropagation();
      if (isDragMode) {
        setIsDragMode(false);
        setDraggedFab(null);
        setDragPosition(null);
        setDragOverIndex(null);
        return;
      }
      telegram.hapticFeedback('medium');
      feedbackService.tap();
      config.onClick();
    };

    return (
      <Box 
        key={fabId}
        ref={(el: HTMLElement | null) => { if (el) fabRefs.current.set(fabId, el); }}
        sx={{ ...wrapperSx, ...dropIndicatorStyle }}
      >
        <Tooltip 
          title={config.label} 
          placement={
            zone === 'bottom' ? 'top' : 
            zone === 'top' ? 'bottom' : 
            zone === 'left' ? 'right' : 'left'
          }
        >
          <IconButton
            onClick={handleClick}
            onTouchStart={(e) => handleLongPressStart(fabId, e)}
            onTouchEnd={handleLongPressEnd}
            onMouseDown={(e) => handleLongPressStart(fabId, e)}
            onMouseUp={handleLongPressEnd}
            onMouseLeave={handleLongPressEnd}
            sx={{
              ...fabSx,
              background: `linear-gradient(135deg, ${config.color}30 0%, ${config.color}50 100%)`,
              backdropFilter: 'blur(12px)',
              border: isDragMode 
                ? `${FAB_BORDER_WIDTH}px dashed ${cyber.neonPurple}` 
                : `${FAB_BORDER_WIDTH}px solid ${config.color}90`,
              boxShadow: `0 4px 20px ${config.color}60, inset 0 0 15px ${config.color}20`,
              '&:hover': {
                transform: 'scale(1.08)',
                boxShadow: `0 6px 25px ${config.color}70, inset 0 0 20px ${config.color}30`,
                border: `${FAB_BORDER_WIDTH}px solid ${config.color}`,
              },
              '&:active': {
                transform: 'scale(0.95)',
              },
            }}
          >
            {config.badge ? (
              <Badge 
                badgeContent={config.badge} 
                max={99}
                sx={{
                  '& .MuiBadge-badge': {
                    backgroundColor: '#ef4444', // Bright red for visibility
                    color: '#ffffff',
                    fontSize: '0.85rem',
                    fontWeight: 900,
                    minWidth: 24,
                    height: 24,
                    borderRadius: '12px',
                    border: '2px solid #fff',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.5), 0 0 12px #ef4444',
                    top: -4,
                    right: -4,
                  }
                }}
              >
                {config.icon}
              </Badge>
            ) : (
              config.icon
            )}
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  // ============================================================================
  // ZONE RENDERERS
  // ============================================================================

  const renderTopToolbar = () => {
    const topFabs = layout.top;
    
    return (
      <Box
        onClick={(e) => {
          // Only hide if clicking the toolbar background, not a FAB
          if (e.target === e.currentTarget || (e.target as HTMLElement).closest('[data-toolbar-bg]')) {
            toggleToolbar('top', false);
          }
        }}
        data-toolbar-bg
        sx={{
          position: 'fixed',
          top: 8,
          left: 12,
          right: 12,
          height: TOOLBAR_HEIGHT,
          display: visible ? 'flex' : 'none',
          alignItems: 'center',
          justifyContent: 'flex-start',
          zIndex: 1100,
          backgroundColor: `${cyber.bgCard}25`,
          backdropFilter: 'blur(12px)',
          borderRadius: 3,
          boxShadow: dragOverZone === 'top' ? `inset 0 0 30px ${cyber.neonCyan}80, 0 0 20px ${cyber.neonCyan}40` : 'none',
          border: dragOverZone === 'top' ? `2px solid ${cyber.neonCyan}60` : '2px solid transparent',
          // Tap-to-hide animation
          transform: toolbarVisibility.top ? 'translateY(0)' : 'translateY(-120%)',
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s ease, border 0.2s ease',
          cursor: 'pointer',
        }}
      >
        {/* Scrollable FAB container with iOS bounce */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          gap: 0.75,
          flex: 1,
          overflowX: 'auto',
          overflowY: 'visible',
          px: 1.5,
          py: 1.25,
          WebkitOverflowScrolling: 'touch', // iOS momentum scrolling
          scrollSnapType: 'x proximity',
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
          // iOS bounce effect
          overscrollBehaviorX: 'contain',
        }}>
          {topFabs.map((id, index) => renderFab(id, 'top', index))}
        </Box>

      </Box>
    );
  };

  const renderLeftColumn = () => {
    const leftFabs = layout.left;
    
    return (
      <Box
        onClick={(e) => {
          if (e.target === e.currentTarget || (e.target as HTMLElement).closest('[data-toolbar-bg]')) {
            toggleToolbar('left', false);
          }
        }}
        data-toolbar-bg
        sx={{
          position: 'fixed',
          top: VERTICAL_TOP_OFFSET,
          left: 12,
          bottom: VERTICAL_BOTTOM_OFFSET,
          width: VERTICAL_COLUMN_WIDTH,
          display: visible ? 'flex' : 'none',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          py: 0.9,
          px: 0.75,
          zIndex: 1099,
          overflowY: 'auto',
          overflowX: 'visible',
          WebkitOverflowScrolling: 'touch', // iOS momentum scrolling
          scrollSnapType: 'y proximity',
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
          overscrollBehaviorY: 'contain', // iOS bounce
          borderRadius: 3,
          boxShadow: dragOverZone === 'left' ? `inset 0 0 30px ${cyber.neonCyan}60, 0 0 20px ${cyber.neonCyan}40` : 'none',
          border: dragOverZone === 'left' ? `2px solid ${cyber.neonCyan}60` : '2px solid transparent',
          // Tap-to-hide animation
          transform: toolbarVisibility.left ? 'translateX(0)' : 'translateX(-120%)',
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s ease, border 0.2s ease',
          cursor: 'pointer',
          // Vertical edge fade hints
          maskImage: leftFabs.length > 4 
            ? 'linear-gradient(to bottom, transparent, black 15px, black calc(100% - 15px), transparent)'
            : 'none',
          WebkitMaskImage: leftFabs.length > 4 
            ? 'linear-gradient(to bottom, transparent, black 15px, black calc(100% - 15px), transparent)'
            : 'none',
        }}
      >
        {leftFabs.map((id, index) => renderFab(id, 'left', index))}
      </Box>
    );
  };

  const renderRightColumn = () => {
    const rightFabs = layout.right;
    
    return (
      <Box
        onClick={(e) => {
          if (e.target === e.currentTarget || (e.target as HTMLElement).closest('[data-toolbar-bg]')) {
            toggleToolbar('right', false);
          }
        }}
        data-toolbar-bg
        sx={{
          position: 'fixed',
          top: VERTICAL_TOP_OFFSET,
          right: 12,
          bottom: VERTICAL_BOTTOM_OFFSET,
          width: VERTICAL_COLUMN_WIDTH,
          display: visible ? 'flex' : 'none',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          py: 0.9,
          px: 0.75,
          zIndex: 1099,
          overflowY: 'auto',
          overflowX: 'visible',
          WebkitOverflowScrolling: 'touch', // iOS momentum scrolling
          scrollSnapType: 'y proximity',
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
          overscrollBehaviorY: 'contain', // iOS bounce
          borderRadius: 3,
          boxShadow: dragOverZone === 'right' ? `inset 0 0 30px ${cyber.neonPurple}60, 0 0 20px ${cyber.neonPurple}40` : 'none',
          border: dragOverZone === 'right' ? `2px solid ${cyber.neonPurple}60` : '2px solid transparent',
          // Tap-to-hide animation
          transform: toolbarVisibility.right ? 'translateX(0)' : 'translateX(120%)',
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s ease, border 0.2s ease',
          cursor: 'pointer',
          // Vertical edge fade hints
          maskImage: rightFabs.length > 6 
            ? 'linear-gradient(to bottom, transparent, black 15px, black calc(100% - 15px), transparent)'
            : 'none',
          WebkitMaskImage: rightFabs.length > 6 
            ? 'linear-gradient(to bottom, transparent, black 15px, black calc(100% - 15px), transparent)'
            : 'none',
        }}
      >
        {rightFabs.map((id, index) => renderFab(id, 'right', index))}
      </Box>
    );
  };

  const renderBottomFooter = () => {
    const bottomFabs = layout.bottom;
    
    return (
      <Box
        onClick={(e) => {
          if (e.target === e.currentTarget || (e.target as HTMLElement).closest('[data-toolbar-bg]')) {
            toggleToolbar('bottom', false);
          }
        }}
        data-toolbar-bg
        sx={{
          position: 'fixed',
          bottom: 8,
          left: 12,
          right: 12,
          height: FOOTER_HEIGHT,
          paddingBottom: 'env(safe-area-inset-bottom, 4px)', // Account for iPhone notch
          backgroundColor: `${cyber.bgCard}25`,
          backdropFilter: 'blur(20px)',
          borderRadius: 3,
          border: dragOverZone === 'bottom' ? `2px solid ${cyber.neonGreen}60` : '2px solid transparent',
          display: visible ? 'flex' : 'none',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1100,
          boxShadow: dragOverZone === 'bottom' ? `inset 0 0 30px ${cyber.neonGreen}60, 0 -4px 20px ${cyber.neonGreen}40` : 'none',
          // Tap-to-hide animation
          transform: toolbarVisibility.bottom ? 'translateY(0)' : 'translateY(120%)',
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s ease, border 0.2s ease',
          cursor: 'pointer',
        }}
      >
        {/* Single-row horizontal scroll with iOS bounce - NO wrapping */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'nowrap',
            gap: 0.75,
            alignItems: 'center',
            justifyContent: 'flex-start',
            overflowX: 'auto',
            overflowY: 'visible',
            width: '100%',
            px: 1.5,
            py: 1.25,
            WebkitOverflowScrolling: 'touch', // iOS momentum scrolling
            scrollSnapType: 'x proximity',
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
            // iOS bounce effect
            overscrollBehaviorX: 'contain',
            // Edge fade hints when scrollable
            maskImage: bottomFabs.length > 5 
              ? 'linear-gradient(to right, transparent, black 20px, black calc(100% - 20px), transparent)'
              : 'none',
            WebkitMaskImage: bottomFabs.length > 5 
              ? 'linear-gradient(to right, transparent, black 20px, black calc(100% - 20px), transparent)'
              : 'none',
          }}
        >
          {bottomFabs.map((id, index) => renderFab(id, 'bottom', index))}
        </Box>
      </Box>
    );
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  // Get the icon for the dragged FAB
  const getDraggedFabIcon = () => {
    if (!draggedFab) return null;
    
    // Each FAB has a UNIQUE color - must match getFabConfig colors!
    const iconMap: Record<string, ReactNode> = {
      hamburger: <MenuIcon sx={{ fontSize: ICON_SIZE, color: cyber.neonCyan }} />,
      settings: <SettingsIcon sx={{ fontSize: ICON_SIZE, color: '#8b5cf6' }} />, // Violet-500
      favorites: <StarIcon sx={{ fontSize: ICON_SIZE, color: cyber.neonPurple }} />,
      voice: <RecordVoiceOverIcon sx={{ fontSize: ICON_SIZE, color: '#22d3ee' }} />, // Bright Cyan
      search: <LocalGasStationIcon sx={{ fontSize: ICON_SIZE, color: '#fb923c' }} />, // Bright Orange
      manualSearch: <SearchIcon sx={{ fontSize: ICON_SIZE, color: '#78716c' }} />, // Stone-500
      map: <MapIcon sx={{ fontSize: ICON_SIZE, color: cyber.neonGold }} />,
      crews: <GroupIcon sx={{ fontSize: ICON_SIZE, color: '#38bdf8' }} />, // Sky Blue
      routes: <RouteIcon sx={{ fontSize: ICON_SIZE, color: '#60a5fa' }} />,
      activity: <TrendingUpIcon sx={{ fontSize: ICON_SIZE, color: '#ec4899' }} />, // Pink-500
      services: <BuildIcon sx={{ fontSize: ICON_SIZE, color: '#84cc16' }} />, // Lime-500
      share: <ShareIcon sx={{ fontSize: ICON_SIZE, color: '#0ea5e9' }} />, // Sky-500
      gpsToggle: <GpsFixedIcon sx={{ fontSize: ICON_SIZE, color: '#2dd4bf' }} />, // Teal
      qrShare: <QrCode2Icon sx={{ fontSize: ICON_SIZE, color: '#3b82f6' }} />, // Blue-500
      suggestions: <FeedbackIcon sx={{ fontSize: ICON_SIZE, color: '#10b981' }} />, // Emerald-500
      networkStatus: <SignalWifi4BarIcon sx={{ fontSize: ICON_SIZE, color: cyber.neonGreen }} />,
      trash: <DeleteIcon sx={{ fontSize: ICON_SIZE, color: '#f59e0b' }} />, // Amber
      // Special FABs - now properly included for drag ghost icon
      ai: <span style={{ 
        fontSize: ICON_SIZE, 
        fontWeight: 800, 
        color: darkMode ? '#ffffff' : '#db2777', 
        fontFamily: 'monospace',
        textShadow: darkMode ? '0 0 8px rgba(255, 255, 255, 0.5)' : 'none',
      }}>AI</span>, // White in dark mode, Pink in light mode
      sync: <SyncIcon sx={{ fontSize: ICON_SIZE, color: cyber.neonGreen }} />,
      timer: <TimerIcon sx={{ fontSize: ICON_SIZE, color: cyber.neonOrange }} />,
      friends: <PeopleIcon sx={{ fontSize: ICON_SIZE, color: '#d946ef' }} />, // Fuchsia-500
      'basin-community': <GroupsIcon sx={{ fontSize: ICON_SIZE, color: '#14b8a6' }} />, // Teal-500
      copAlert: <LocalPoliceIcon sx={{ fontSize: ICON_SIZE, color: '#3b82f6' }} />, // Blue - police alert
      support: <SupportAgentIcon sx={{ fontSize: ICON_SIZE, color: '#6366f1' }} />, // Indigo-500
      jobs: <WorkIcon sx={{ fontSize: ICON_SIZE, color: '#f43f5e' }} />, // Rose-500
    };
    
    return iconMap[draggedFab] || null;
  };

  const arrowAccentTop = arrowAccents.top;
  const arrowAccentBottom = arrowAccents.bottom;
  const arrowAccentLeft = arrowAccents.left;
  const arrowAccentRight = arrowAccents.right;

  // Restore a FAB from archive to bottom toolbar
  const restoreFromTrash = useCallback((fabId: string) => {
    const newLayout = { ...layout };
    newLayout.archived = layout.archived.filter(id => id !== fabId);
    newLayout.bottom = [...layout.bottom, fabId];
    saveLayout(newLayout);
    telegram.hapticFeedback('success');
    feedbackService.playSound('success');
    setHazardToast({ open: true, message: `✅ Restored`, severity: 'success' });
  }, [layout, saveLayout]);

  return (
    <>
      {renderTopToolbar()}
      {renderLeftColumn()}
      {renderRightColumn()}
      {renderBottomFooter()}

      {showSettingsPopout && (
        <>
          <Box
            onClick={onShowSettings}
            sx={{
              position: 'fixed',
              inset: 0,
              zIndex: 1199,
              backgroundColor: 'transparent',
            }}
          />
          <Box
            sx={{
              position: 'fixed',
              bottom: FOOTER_HEIGHT,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: 1,
              zIndex: 1200,
              animation: 'slideUp 0.2s ease-out',
              '@keyframes slideUp': {
                from: { opacity: 0, transform: 'translateX(-50%) translateY(20px)' },
                to: { opacity: 1, transform: 'translateX(-50%) translateY(0)' },
              },
            }}
          >
            <Tooltip title={darkMode ? t('lightMode') : t('darkMode')} arrow placement="top">
              <IconButton
                onClick={() => {
                  onToggleDarkMode();
                  feedbackService.toggle();
                  onShowSettings();
                }}
                sx={{
                  width: 52,
                  height: 52,
                  background: darkMode
                    ? `linear-gradient(135deg, ${cyber.neonPink}30 0%, ${cyber.neonRed}45 100%)`
                    : `linear-gradient(135deg, ${cyber.neonOrange}25 0%, ${cyber.neonGold}40 100%)`,
                  border: darkMode ? `3px solid ${cyber.neonRed}70` : `3px solid ${cyber.neonOrange}70`,
                  boxShadow: darkMode
                    ? `0 0 18px ${cyber.neonRed}60`
                    : `0 0 18px ${cyber.neonOrange}50`,
                  color: darkMode ? cyber.neonRed : cyber.neonOrange,
                  '&:hover': {
                    transform: 'scale(1.1)',
                    boxShadow: darkMode
                      ? `0 0 24px ${cyber.neonRed}80`
                      : `0 0 24px ${cyber.neonOrange}70`,
                  },
                }}
              >
                {darkMode ? (
                  <LightModeIcon sx={{ fontSize: 26, color: cyber.neonOrange }} />
                ) : (
                  <DarkModeIcon sx={{ fontSize: 26, color: cyber.neonRed }} />
                )}
              </IconButton>
            </Tooltip>

            <Tooltip title={highContrast ? t('normalContrast') : t('highContrast')} arrow placement="top">
              <IconButton
                onClick={() => {
                  onToggleHighContrast();
                  feedbackService.toggle();
                  onShowSettings();
                }}
                sx={{
                  width: 52,
                  height: 52,
                  background: highContrast
                    ? 'linear-gradient(135deg, rgba(253, 224, 71, 0.35) 0%, rgba(250, 204, 21, 0.5) 100%)'
                    : 'linear-gradient(135deg, rgba(107, 114, 128, 0.2) 0%, rgba(75, 85, 99, 0.3) 100%)',
                  border: highContrast ? '3px solid rgba(253, 224, 71, 0.8)' : '3px solid rgba(107, 114, 128, 0.5)',
                  boxShadow: highContrast
                    ? '0 0 18px rgba(253, 224, 71, 0.6)'
                    : '0 0 14px rgba(107, 114, 128, 0.4)',
                  color: highContrast ? '#fde047' : darkMode ? '#9ca3af' : '#4b5563',
                  '&:hover': {
                    transform: 'scale(1.1)',
                    boxShadow: highContrast
                      ? '0 0 26px rgba(253, 224, 71, 0.8)'
                      : '0 0 18px rgba(107, 114, 128, 0.6)',
                  },
                }}
              >
                <ContrastIcon sx={{ fontSize: 26, color: highContrast ? '#fde047' : darkMode ? '#9ca3af' : '#4b5563' }} />
              </IconButton>
            </Tooltip>

            <Tooltip title={t('toggleLanguage')} arrow placement="top">
              <IconButton
                onClick={() => {
                  onToggleLanguage();
                  feedbackService.toggle();
                  onShowSettings();
                }}
                sx={{
                  width: 52,
                  height: 52,
                  background: language === 'es'
                    ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(220, 38, 38, 0.3) 100%)'
                    : 'linear-gradient(135deg, rgba(14, 165, 233, 0.2) 0%, rgba(6, 182, 212, 0.35) 100%)',
                  border: language === 'es'
                    ? '3px solid rgba(239, 68, 68, 0.6)'
                    : '3px solid rgba(6, 182, 212, 0.6)',
                  boxShadow: language === 'es'
                    ? '0 0 18px rgba(239, 68, 68, 0.6)'
                    : '0 0 18px rgba(6, 182, 212, 0.5)',
                  color: language === 'es' ? '#ef4444' : '#06b6d4',
                  '&:hover': {
                    transform: 'scale(1.1)',
                    boxShadow: language === 'es'
                      ? '0 0 26px rgba(239, 68, 68, 0.8)'
                      : '0 0 26px rgba(6, 182, 212, 0.7)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Box sx={{ fontSize: 22 }}>
                    {language === 'en' ? '🇲🇽' : '🇺🇸'}
                  </Box>
                  <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, color: language === 'es' ? '#ef4444' : '#06b6d4', mt: -0.3 }}>
                    {language === 'en' ? 'ES' : 'EN'}
                  </Typography>
                </Box>
              </IconButton>
            </Tooltip>

            <Tooltip title="Reset FAB Layout" arrow placement="top">
              <IconButton
                onClick={() => {
                  resetLayout();
                  feedbackService.tap();
                  onShowSettings();
                }}
                sx={{
                  width: 52,
                  height: 52,
                  background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(220, 38, 38, 0.35) 100%)',
                  border: '3px solid rgba(239, 68, 68, 0.6)',
                  boxShadow: '0 0 18px rgba(239, 68, 68, 0.6)',
                  color: '#ef4444',
                  '&:hover': {
                    transform: 'scale(1.1)',
                    boxShadow: '0 0 26px rgba(239, 68, 68, 0.8)',
                  },
                }}
              >
                <RestartAltIcon sx={{ fontSize: 26 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </>
      )}
      
      {/* Road Hazard SpeedDial Menu - Icons only, one-tap reporting */}
      {showHazardMenu && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 100,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 1,
            zIndex: 1200,
            animation: 'slideUp 0.2s ease-out',
            '@keyframes slideUp': {
              from: { opacity: 0, transform: 'translateX(-50%) translateY(20px)' },
              to: { opacity: 1, transform: 'translateX(-50%) translateY(0)' },
            },
          }}
        >
          {[
            { type: 'hazard', icon: <WarningIcon />, color: '#f59e0b' },
            { type: 'wreck', icon: <CarCrashIcon />, color: '#ef4444' },
            { type: 'cop', icon: <LocalPoliceIcon />, color: '#3b82f6' },
            { type: 'roadblock', icon: <BlockIcon />, color: '#8b5cf6' },
            { type: 'fire', icon: <WhatshotIcon />, color: '#f97316' },
          ].map((item) => (
            <IconButton
              key={item.type}
              onClick={async () => {
                setShowHazardMenu(false);
                telegram.hapticFeedback('heavy');
                
                // Get current location
                try {
                  const result = await requestPreciseLocation({
                    desiredAccuracyMeters: 20,
                    refinementTimeoutMs: 7000,
                    allowLastKnownFallback: true,
                    allowTelegramFallback: true,
                  });

                  if (!result.fix) {
                    telegram.hapticFeedback('error');
                    setHazardToast({ open: true, message: '📍 Location required', severity: 'error' });
                    return;
                  }

                  const hazard = await roadHazardsService.reportHazard(
                    item.type as RoadHazard['type'],
                    result.fix.lat,
                    result.fix.lng
                  );
                  
                  if (hazard) {
                    telegram.hapticFeedback('success');
                    feedbackService.playSound('success');
                    const accuracyText =
                      !result.fulfilled && typeof result.fix.accuracy === 'number'
                        ? ` (±${Math.round(result.fix.accuracy)}m)`
                        : '';
                    setHazardToast({ open: true, message: `✅ Reported!${accuracyText}`, severity: 'success' });
                    onHazardReported?.(hazard);
                  } else {
                    telegram.hapticFeedback('error');
                    setHazardToast({ open: true, message: '❌ Failed', severity: 'error' });
                  }
                } catch (error) {
                  console.error('[Hazard] Location error:', error);
                  telegram.hapticFeedback('error');
                  setHazardToast({ open: true, message: '📍 Location required', severity: 'error' });
                }
              }}
              sx={{
                width: 52,
                height: 52,
                background: `linear-gradient(135deg, ${item.color}20 0%, ${item.color}40 100%)`,
                border: `3px solid ${item.color}`,
                boxShadow: `0 0 15px ${item.color}60`,
                color: item.color,
                '&:hover': {
                  transform: 'scale(1.1)',
                  boxShadow: `0 0 25px ${item.color}80`,
                },
              }}
            >
              {item.icon}
            </IconButton>
          ))}
        </Box>
      )}

      {/* Trash Menu - Shows archived FABs for restoration */}
      {showTrashMenu && layout.archived && layout.archived.length > 0 && (
        <>
          <Box
            sx={{
              position: 'fixed',
              top: 90,
              right: 12,
              zIndex: 1250,
              backgroundColor: darkMode ? 'rgba(15, 23, 42, 0.98)' : 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(20px)',
              borderRadius: 3,
              border: `2px solid ${cyber.neonRed}40`,
              boxShadow: `0 8px 32px rgba(0, 0, 0, 0.4), 0 0 20px ${cyber.neonRed}20`,
              padding: 2,
              minWidth: 200,
              maxHeight: 400,
              overflowY: 'auto',
              animation: 'slideDown 0.2s ease-out',
              '@keyframes slideDown': {
                from: { opacity: 0, transform: 'translateY(-10px)' },
                to: { opacity: 1, transform: 'translateY(0)' },
              },
            }}
          >
            <Typography
              sx={{
                fontSize: '0.85rem',
                fontWeight: 700,
                color: cyber.textPrimary,
                mb: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <DeleteIcon sx={{ fontSize: 18, color: '#6b7280' }} />
              Archived FABs ({layout.archived.length})
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {layout.archived.map((fabId) => {
                // Each FAB has a UNIQUE color - must match getFabConfig colors!
                const iconMap: Record<string, ReactNode> = {
                  hamburger: <MenuIcon sx={{ fontSize: 20, color: cyber.neonCyan }} />,
                  settings: <SettingsIcon sx={{ fontSize: 20, color: '#8b5cf6' }} />, // Violet-500
                  favorites: <StarIcon sx={{ fontSize: 20, color: cyber.neonPurple }} />,
                  voice: <RecordVoiceOverIcon sx={{ fontSize: 20, color: '#22d3ee' }} />, // Bright Cyan
                  search: <LocalGasStationIcon sx={{ fontSize: 20, color: '#fb923c' }} />, // Bright Orange
                  manualSearch: <SearchIcon sx={{ fontSize: 20, color: '#78716c' }} />, // Stone-500
                  map: <MapIcon sx={{ fontSize: 20, color: cyber.neonGold }} />,
                  crews: <GroupIcon sx={{ fontSize: 20, color: '#38bdf8' }} />, // Sky Blue
                  routes: <RouteIcon sx={{ fontSize: 20, color: '#60a5fa' }} />,
                  activity: <TrendingUpIcon sx={{ fontSize: 20, color: '#ec4899' }} />, // Pink-500
                  services: <BuildIcon sx={{ fontSize: 20, color: '#84cc16' }} />, // Lime-500
                  share: <ShareIcon sx={{ fontSize: 20, color: '#0ea5e9' }} />, // Sky-500
                  gpsToggle: <GpsFixedIcon sx={{ fontSize: 20, color: '#2dd4bf' }} />, // Teal
                  qrShare: <QrCode2Icon sx={{ fontSize: 20, color: '#3b82f6' }} />, // Blue-500
                  suggestions: <FeedbackIcon sx={{ fontSize: 20, color: '#10b981' }} />, // Emerald-500
                  networkStatus: <SignalWifi4BarIcon sx={{ fontSize: 20, color: cyber.neonGreen }} />,
                  ai: <span style={{ 
                    fontSize: 20, 
                    fontWeight: 800, 
                    color: darkMode ? '#ffffff' : '#db2777', 
                    fontFamily: 'monospace',
                    textShadow: darkMode ? '0 0 4px rgba(255, 255, 255, 0.5)' : 'none',
                  }}>AI</span>, // White in dark mode, Pink in light mode
                  sync: <SyncIcon sx={{ fontSize: 20, color: cyber.neonGreen }} />,
                  timer: <TimerIcon sx={{ fontSize: 20, color: cyber.neonOrange }} />,
                  trash: <DeleteIcon sx={{ fontSize: 20, color: '#f59e0b' }} />, // Amber
                  friends: <PeopleIcon sx={{ fontSize: 20, color: '#d946ef' }} />, // Fuchsia-500
                  'basin-community': <GroupsIcon sx={{ fontSize: 20, color: '#14b8a6' }} />, // Teal-500
                  copAlert: <LocalPoliceIcon sx={{ fontSize: 20, color: '#3b82f6' }} />, // Blue - police alert
                  support: <SupportAgentIcon sx={{ fontSize: 20, color: '#6366f1' }} />, // Indigo-500
                  jobs: <WorkIcon sx={{ fontSize: 20, color: '#f43f5e' }} />, // Rose-500
                };
                
                return (
                  <Box
                    key={fabId}
                    onClick={() => {
                      restoreFromTrash(fabId);
                      setShowTrashMenu(false);
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      padding: '10px 12px',
                      borderRadius: 2,
                      backgroundColor: darkMode ? 'rgba(30, 41, 59, 0.6)' : 'rgba(241, 245, 249, 0.8)',
                      border: `1px solid ${cyber.neonGreen}30`,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: darkMode ? 'rgba(30, 41, 59, 0.9)' : 'rgba(241, 245, 249, 1)',
                        border: `1px solid ${cyber.neonGreen}60`,
                        transform: 'translateX(-2px)',
                      },
                    }}
                  >
                    {iconMap[fabId] || <SettingsIcon sx={{ fontSize: 20 }} />}
                    <Typography sx={{ 
                      fontSize: '0.85rem', 
                      color: cyber.textPrimary,
                      flex: 1,
                      textTransform: 'capitalize',
                    }}>
                      {fabId.replace(/([A-Z])/g, ' $1').trim()}
                    </Typography>
                    <RestoreFromTrashIcon sx={{ fontSize: 18, color: cyber.neonGreen, opacity: 0.7 }} />
                  </Box>
                );
              })}
            </Box>
            
            <Typography
              sx={{
                fontSize: '0.7rem',
                color: cyber.textSecondary,
                textAlign: 'center',
                mt: 1.5,
                opacity: 0.6,
              }}
            >
              Tap to restore
            </Typography>
          </Box>
          
          {/* Backdrop for trash menu */}
          <Box
            onClick={() => { telegram.hapticFeedback('light'); setShowTrashMenu(false); }}
            sx={{
              position: 'fixed',
              inset: 0,
              zIndex: 1249,
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
            }}
          />
        </>
      )}
      
      {/* Drag mode overlay with ghost FAB */}
      {isDragMode && (
        <>
          {/* Dark overlay */}
          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              zIndex: 1200,
              pointerEvents: 'none',
            }}
          />
          
          {/* Instruction text */}
          <Box
            sx={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1201,
              pointerEvents: 'none',
              textAlign: 'center',
            }}
          >
            <Typography
              sx={{
                color: cyber.textPrimary,
                fontSize: '1.1rem',
                fontWeight: 600,
                textShadow: `0 0 20px ${isDragOverTrash ? cyber.neonRed : cyber.neonCyan}`,
                mb: 1,
              }}
            >
              {isDragOverTrash ? '🗑️ Drop to Archive' : 'Drag to zone or 🗑️ Trash'}
            </Typography>
            <Typography
              sx={{
                color: isDragOverTrash ? cyber.neonRed : cyber.textSecondary,
                fontSize: '0.9rem',
              }}
            >
              {isDragOverTrash ? 'Release to archive this FAB' :
               dragOverZone === 'top' ? '📍 Top Toolbar' : 
               dragOverZone === 'left' ? '📍 Left Column' :
               dragOverZone === 'right' ? '📍 Right Column' : 
               dragOverZone === 'bottom' ? '📍 Bottom Bar' : 
               'Move to a highlighted zone'}
            </Typography>
          </Box>
          
          {/* Ghost FAB following cursor */}
          {dragPosition && (
            <Box
              sx={{
                position: 'fixed',
                left: dragPosition.x - FAB_SIZE / 2,
                top: dragPosition.y - FAB_SIZE / 2,
                width: FAB_SIZE,
                height: FAB_SIZE,
                borderRadius: '50%',
                background: isDragOverTrash 
                  ? `linear-gradient(135deg, ${cyber.neonRed}40 0%, ${cyber.neonRed}60 100%)`
                  : `linear-gradient(135deg, ${cyber.neonPurple}40 0%, ${cyber.neonCyan}40 100%)`,
                border: `3px solid ${isDragOverTrash ? cyber.neonRed : cyber.neonPurple}`,
                boxShadow: isDragOverTrash 
                  ? `0 0 30px ${cyber.neonRed}80, 0 0 60px ${cyber.neonRed}40`
                  : `0 0 30px ${cyber.neonPurple}80, 0 0 60px ${cyber.neonCyan}40`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1202,
                pointerEvents: 'none',
                animation: isDragOverTrash ? 'shake 0.3s ease-in-out infinite' : 'pulse 1s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': { transform: 'scale(1)', opacity: 1 },
                  '50%': { transform: 'scale(1.1)', opacity: 0.8 },
                },
                '@keyframes shake': {
                  '0%, 100%': { transform: 'rotate(0deg)' },
                  '25%': { transform: 'rotate(-5deg)' },
                  '75%': { transform: 'rotate(5deg)' },
                },
              }}
            >
              {getDraggedFabIcon()}
            </Box>
          )}
        </>
      )}

      {/* Hazard Toast Notification */}
      <Snackbar
        open={hazardToast.open}
        autoHideDuration={2000}
        onClose={() => setHazardToast(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          severity={hazardToast.severity} 
          sx={{ 
            fontWeight: 'bold',
            fontSize: '1rem',
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
          }}
        >
          {hazardToast.message}
        </Alert>
      </Snackbar>

      {/* ============================================================ */}
      {/* HIDDEN TOOLBAR INDICATORS & SHOW ALL BUTTON                   */}
      {/* ============================================================ */}

      {/* Top edge arrow indicator when top toolbar is hidden */}
      {!toolbarVisibility.top && visible && (
        <Box
          onClick={() => toggleToolbar('top', true)}
          sx={{
            position: 'fixed',
            top: 8,
            left: '50%',
            transform: 'translateX(-50%)',
            width: ARROW_INDICATOR_SIZE,
            height: ARROW_INDICATOR_SIZE,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${withAlpha(arrowAccentTop, 0.25)} 0%, ${withAlpha(arrowAccentTop, 0.55)} 100%)`,
            backdropFilter: 'blur(10px)',
            border: `2px solid ${withAlpha(arrowAccentTop, 0.7)}`,
            cursor: 'pointer',
            pointerEvents: 'auto',
            zIndex: 1200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 4px 18px ${withAlpha(arrowAccentTop, 0.35)}, 0 0 18px ${withAlpha(arrowAccentTop, 0.45)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            animation: 'bounceDown 1.5s ease-in-out infinite',
            '@keyframes bounceDown': {
              '0%, 100%': { transform: 'translateX(-50%) translateY(0)' },
              '50%': { transform: 'translateX(-50%) translateY(6px)' },
            },
            '&:hover': {
              background: `linear-gradient(135deg, ${withAlpha(arrowAccentTop, 0.4)} 0%, ${withAlpha(arrowAccentTop, 0.7)} 100%)`,
              borderColor: arrowAccentTop,
              boxShadow: `0 6px 24px ${withAlpha(arrowAccentTop, 0.4)}, 0 0 26px ${withAlpha(arrowAccentTop, 0.6)}`,
            },
            '&:active': {
              transform: 'translateX(-50%) scale(0.92)',
              animation: 'none',
            },
          }}
        >
          <KeyboardArrowDownIcon sx={{ fontSize: ARROW_ICON_SIZE, color: arrowAccentTop }} />
        </Box>
      )}

      {/* Bottom edge arrow indicator when bottom toolbar is hidden */}
      {!toolbarVisibility.bottom && visible && (
        <Box
          onClick={() => toggleToolbar('bottom', true)}
          sx={{
            position: 'fixed',
            bottom: 8,
            left: '50%',
            transform: 'translateX(-50%)',
            width: ARROW_INDICATOR_SIZE,
            height: ARROW_INDICATOR_SIZE,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${withAlpha(arrowAccentBottom, 0.25)} 0%, ${withAlpha(arrowAccentBottom, 0.55)} 100%)`,
            backdropFilter: 'blur(10px)',
            border: `2px solid ${withAlpha(arrowAccentBottom, 0.7)}`,
            cursor: 'pointer',
            pointerEvents: 'auto',
            zIndex: 1200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 4px 18px ${withAlpha(arrowAccentBottom, 0.35)}, 0 0 18px ${withAlpha(arrowAccentBottom, 0.45)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            animation: 'bounceUp 1.5s ease-in-out infinite',
            '@keyframes bounceUp': {
              '0%, 100%': { transform: 'translateX(-50%) translateY(0)' },
              '50%': { transform: 'translateX(-50%) translateY(-6px)' },
            },
            '&:hover': {
              background: `linear-gradient(135deg, ${withAlpha(arrowAccentBottom, 0.4)} 0%, ${withAlpha(arrowAccentBottom, 0.7)} 100%)`,
              borderColor: arrowAccentBottom,
              boxShadow: `0 6px 24px ${withAlpha(arrowAccentBottom, 0.4)}, 0 0 26px ${withAlpha(arrowAccentBottom, 0.6)}`,
            },
            '&:active': {
              transform: 'translateX(-50%) scale(0.92)',
              animation: 'none',
            },
          }}
        >
          <KeyboardArrowUpIcon sx={{ fontSize: ARROW_ICON_SIZE, color: arrowAccentBottom }} />
        </Box>
      )}

      {/* Left edge arrow indicator when left toolbar is hidden */}
      {!toolbarVisibility.left && visible && (
        <Box
          onClick={() => toggleToolbar('left', true)}
          sx={{
            position: 'fixed',
            left: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            width: ARROW_INDICATOR_SIZE,
            height: ARROW_INDICATOR_SIZE,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${withAlpha(arrowAccentLeft, 0.25)} 0%, ${withAlpha(arrowAccentLeft, 0.55)} 100%)`,
            backdropFilter: 'blur(10px)',
            border: `2px solid ${withAlpha(arrowAccentLeft, 0.7)}`,
            cursor: 'pointer',
            pointerEvents: 'auto',
            zIndex: 1200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 4px 18px ${withAlpha(arrowAccentLeft, 0.35)}, 0 0 18px ${withAlpha(arrowAccentLeft, 0.45)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            animation: 'bounceRight 1.5s ease-in-out infinite',
            '@keyframes bounceRight': {
              '0%, 100%': { transform: 'translateY(-50%) translateX(0)' },
              '50%': { transform: 'translateY(-50%) translateX(6px)' },
            },
            '&:hover': {
              background: `linear-gradient(135deg, ${withAlpha(arrowAccentLeft, 0.4)} 0%, ${withAlpha(arrowAccentLeft, 0.7)} 100%)`,
              borderColor: arrowAccentLeft,
              boxShadow: `0 6px 24px ${withAlpha(arrowAccentLeft, 0.4)}, 0 0 26px ${withAlpha(arrowAccentLeft, 0.6)}`,
            },
            '&:active': {
              transform: 'translateY(-50%) scale(0.92)',
              animation: 'none',
            },
          }}
        >
          <KeyboardArrowRightIcon sx={{ fontSize: ARROW_ICON_SIZE, color: arrowAccentLeft }} />
        </Box>
      )}

      {/* Right edge arrow indicator when right toolbar is hidden */}
      {!toolbarVisibility.right && visible && (
        <Box
          onClick={() => toggleToolbar('right', true)}
          sx={{
            position: 'fixed',
            right: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            width: ARROW_INDICATOR_SIZE,
            height: ARROW_INDICATOR_SIZE,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${withAlpha(arrowAccentRight, 0.25)} 0%, ${withAlpha(arrowAccentRight, 0.55)} 100%)`,
            backdropFilter: 'blur(10px)',
            border: `2px solid ${withAlpha(arrowAccentRight, 0.7)}`,
            cursor: 'pointer',
            pointerEvents: 'auto',
            zIndex: 1200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 4px 18px ${withAlpha(arrowAccentRight, 0.35)}, 0 0 18px ${withAlpha(arrowAccentRight, 0.45)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            animation: 'bounceLeft 1.5s ease-in-out infinite',
            '@keyframes bounceLeft': {
              '0%, 100%': { transform: 'translateY(-50%) translateX(0)' },
              '50%': { transform: 'translateY(-50%) translateX(-6px)' },
            },
            '&:hover': {
              background: `linear-gradient(135deg, ${withAlpha(arrowAccentRight, 0.4)} 0%, ${withAlpha(arrowAccentRight, 0.7)} 100%)`,
              borderColor: arrowAccentRight,
              boxShadow: `0 6px 24px ${withAlpha(arrowAccentRight, 0.4)}, 0 0 26px ${withAlpha(arrowAccentRight, 0.6)}`,
            },
            '&:active': {
              transform: 'translateY(-50%) scale(0.92)',
              animation: 'none',
            },
          }}
        >
          <KeyboardArrowLeftIcon sx={{ fontSize: ARROW_ICON_SIZE, color: arrowAccentRight }} />
        </Box>
      )}

      {/* Show All Toolbars floating button - appears when any toolbar is hidden */}
      {anyToolbarHidden && visible && !isDragMode && (
        <Box
          onClick={showAllToolbars}
          sx={{
            position: 'fixed',
            bottom: toolbarVisibility.bottom ? 96 : 60,
            right: toolbarVisibility.right ? 80 : 16,
            width: 44,
            height: 44,
            borderRadius: '50%',
            backgroundColor: `${cyber.bgCard}95`,
            backdropFilter: 'blur(10px)',
            border: `2px solid ${cyber.neonCyan}60`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            pointerEvents: 'auto',
            zIndex: 1150,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: `0 4px 20px rgba(0,0,0,0.3), 0 0 15px ${cyber.neonCyan}30`,
            '&:hover': {
              transform: 'scale(1.1)',
              boxShadow: `0 4px 25px rgba(0,0,0,0.4), 0 0 25px ${cyber.neonCyan}50`,
              borderColor: cyber.neonCyan,
            },
            '&:active': {
              transform: 'scale(0.95)',
            },
          }}
        >
          <VisibilityIcon sx={{ fontSize: 22, color: '#3b82f6' }} />
        </Box>
      )}
    </>
  );
}

export default UnifiedFabManager;
